import Flex from '@deprecated-ui/common/Flex';
import { useEffect, useState } from 'react';
import { parseExistImage } from 'utils/stream';
import ERROR_MESSAGE from 'constants/errorMessage';
import { selectors as profileSelector } from 'lib/entities/profile';
import { useSelector } from 'react-redux';
import alertMessage from 'global/AlertMessage';
import FileContainer from './FileContainer';
import Footer from './Footer';
import ImageList from './ImageContainer';
import Polling from './Polling';
import {
  ComposeBox,
  ComposeContent,
  ComposePostContent,
  ImageListContainer,
} from './styled';
import TargetPrice from './TargetPrice';
import {
  COMMENT_SETTING_LIST,
  COMPOSE_MODE,
  initialPolling,
  initialTargetPrice,
  ADMIN_USER_PRIVILEGE as isAdminUser,
} from './constants';
import RepostBox from './RepostBox';
import Textarea from './Textarea';
import Header from './Header';
import { IImageItem, StreamComposeProps, StreamSubmitPayload } from './types';
import ModalFailedPost from './Modal/ModalFailedPost';
import ModalMaximumSize from './Modal/ModalMaximumSize';
import useDropFile from './useDropFile';

function StreamCompose({
  onCancel = () => {},
  onSubmit,
  isLoading,
  mode = COMPOSE_MODE.COMPOSE,
  initialValues,
  post,
  autoExpand,
  showPinned = false,
}: StreamComposeProps) {
  const {
    data: { avatar, username, user_priv },
  } = useSelector(profileSelector);
  const defaultExpand = mode !== COMPOSE_MODE.COMPOSE;
  const [isExpand, setIsExpand] = useState(defaultExpand);
  const showPinButton = isAdminUser.includes(user_priv) && showPinned;

  // Cannot get component ref using useRef
  const [textAreaComponent, setTextAreaComponent] = useState(null);

  // documents' state
  const [documents, setDocuments] = useState([]);
  const [images, setImages] = useState<IImageItem[]>([]);

  // valueState
  const [content, setContent] = useState('');
  const [polling, setPolling] = useState(initialPolling);
  const [targetPrice, setTargetPrice] = useState(initialTargetPrice);
  const [commenter, setCommenter] = useState(COMMENT_SETTING_LIST[0]);
  const [postTypeComment, setPostTypeComment] = useState<boolean>(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorModalMaxSizeVisible, setErrorModalMaxSizeVisible] =
    useState(false);
  const [pinnedPost, setPinnedPost] = useState<boolean>(false);

  const { dropzoneRef, handleOnDragOver, handleOnDragStart, handleOnDrop } =
    useDropFile({
      isAllowedToUpload: mode !== COMPOSE_MODE.SHARE && isExpand,
      onErrorMaxSize: setErrorModalMaxSizeVisible,
      setDocuments,
      setImages,
      images,
      documents,
    });

  useEffect(() => {
    setTextAreaComponent(document.getElementById('stream-compose-textarea'));
  }, []);

  // Did mount on edit post
  useEffect(() => {
    const isInitialOpen =
      content === '' &&
      polling === initialPolling &&
      targetPrice === initialTargetPrice &&
      documents.length === 0 &&
      images.length === 0;
    // set initial content
    if (initialValues && isInitialOpen) {
      // update states
      setContent(initialValues.content);
      setDocuments(initialValues.documents);
      const listImage = parseExistImage(initialValues.images_data ?? []);
      setImages(listImage);

      //  if there's target price data
      if (initialValues.targetPrice) {
        setTargetPrice({
          ...initialValues.targetPrice,
          isOpen: true,
        });
      }

      if (initialValues?.commenter_type) {
        const commenterType = COMMENT_SETTING_LIST.find(
          (comment) => comment.type === initialValues?.commenter_type,
        );
        setCommenter(commenterType);
      }
    }
    // set post type if post is comment or not
    if (initialValues && COMPOSE_MODE.EDIT === mode) {
      const { postId, parentPostId } = initialValues;

      if (postId !== parentPostId && parentPostId !== 0) {
        setPostTypeComment(true);
      }
    }

    // set to expanded, of course
    if (autoExpand) {
      setIsExpand(true);
    }
  }, [autoExpand, initialValues]);

  const handleToggleExpand = () => {
    setIsExpand(!isExpand);
  };

  const handleReset = () => {
    const initialContent = initialValues?.content ?? '';
    setContent(initialContent);
    setDocuments([]);
    setImages([]);
    setTargetPrice(initialTargetPrice);
    setPolling(initialPolling);
    setPinnedPost(false);
  };

  const handleChangeContent = (text) => {
    setContent(text);
  };

  const handleSelectEmoji = (emoji) => {
    // @ts-ignore
    if (textAreaComponent?.selectionStart !== undefined) {
      // @ts-ignore
      const insertEmojiIndex = textAreaComponent?.selectionStart;
      setContent(
        `${`${content.slice(0, insertEmojiIndex)} ${emoji}${content.slice(
          insertEmojiIndex,
        )}`}`,
      );

      setTimeout(() => {
        const insertEmojiIndexEnd = insertEmojiIndex + 3; // (space)emoji
        textAreaComponent?.focus();
        // @ts-ignore
        textAreaComponent?.setSelectionRange(
          insertEmojiIndexEnd,
          insertEmojiIndexEnd,
        );
      }, 0);

      return;
    }
    setContent(`${content} ${emoji} `);
  };

  const handleChangePolling = (data) => {
    setPolling({
      ...polling,
      ...data,
    });
  };

  const handleTogglePolling = (isOpen) => {
    setPolling({
      ...initialPolling,
      isOpen,
    });
    setTargetPrice(initialTargetPrice);

    // reset attachment data if polling opened
    if (isOpen) {
      setDocuments([]);
      setImages([]);
    }
  };

  const handleChangeTargetPrice = (data) => {
    setTargetPrice({
      ...targetPrice,
      ...data,
    });
  };

  const handleToggleTargetPrice = (isOpen) => {
    setTargetPrice({
      ...initialTargetPrice,
      isOpen,
    });

    if (isOpen) {
      setPolling(initialPolling);
    }
  };

  const handleTogglePinned = () => {
    setPinnedPost(!pinnedPost);
  };

  const handleChangeCommenter = (data) => {
    setCommenter(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content) {
      alertMessage({
        content: ERROR_MESSAGE.STREAM_TEXT_REQUIRED,
        alertType: 'plain-default',
        messageType: 'error',
      });
      return;
    }

    if (
      targetPrice.isOpen &&
      (targetPrice.target_price <= 0 || targetPrice.target_price === undefined)
    ) {
      alertMessage({
        content: ERROR_MESSAGE.STREAM_TARGET_PRICE_REQUIRED,
        alertType: 'plain-default',
        messageType: 'error',
      });
      return;
    }
    const {
      isOpen: isopenTarget,
      target_price: price,
      ...otherTargetData
    } = targetPrice;

    // Remove extra newline of string
    const contentText = `${content.trim().replace(/\n{3,}/gm, '\n\n')}`;
    let data: StreamSubmitPayload = {
      content: contentText,
      image: images,
      file: documents,
      ...otherTargetData,
      target_price: price.toFixed(6),
      commenter_type: commenter.type,
    };

    // inject polling data if polling opened
    if (polling.isOpen && mode !== COMPOSE_MODE.EDIT) {
      const {
        isOpen,
        duration,
        polling_options: options,
        ...otherPollingData
      } = polling;
      data = {
        ...data,
        polling: {
          ...otherPollingData,
          polling_options: JSON.stringify(options),
        },
      };
    }

    // post with pinned post
    if (pinnedPost) {
      data.isPinned = pinnedPost;
    }

    const submit = await onSubmit(data);

    const isError = submit instanceof Error;
    const showRetryModal =
      isError &&
      (images.length > 0 || documents.length > 0) &&
      submit?.message === ERROR_MESSAGE.STREAM_ERROR_UPLOAD_FILE;

    // Check if it can be retryable
    if (showRetryModal) {
      setErrorModalVisible(true);
    }

    const isErrorResponse = submit?.response?.status >= 500;
    if ((isError && submit?.message === 'Network Error') || isErrorResponse) {
      alertMessage({
        content:
          'Postingan kamu gagal terkirim. Coba lagi untuk mengirim ulang.',
        alertType: 'snackbar',
        messageType: 'error',
        hasCloseIcon: true,
      });
    }

    const isSuccess = !isError && !submit;
    if (isSuccess) {
      handleReset();
      setIsExpand(false);
      setErrorModalVisible(false);
    }
  };

  const handleCancel = () => {
    // reset local state and redux
    handleReset();
    setIsExpand(false);

    // if user want to add additional cancel
    // like close modal on click cancel buton, etc.
    onCancel();
  };

  const handleResetImage = (i) => {
    const newImages = Array.from(images);
    newImages.splice(i, 1);
    setImages(newImages);
  };

  const handleCloseFailedModal = () => {
    setErrorModalVisible(false);
    handleReset();
    setIsExpand(false);
  };

  return (
    <ComposeBox noBorder={mode !== COMPOSE_MODE.COMPOSE}>
      <div
        onDragEnter={handleOnDragStart}
        onDragOver={handleOnDragOver}
        onDrop={handleOnDrop}
        ref={dropzoneRef}
      >
        <form onSubmit={handleSubmit}>
          <Header
            avatar={avatar}
            username={username}
            isExpand={isExpand}
            commenter={commenter}
            mode={mode}
            onExpand={handleToggleExpand}
            onChangeCommenter={handleChangeCommenter}
          />
          <Flex width="100%">
            <ComposeContent isExpand={isExpand}>
              <ComposePostContent mode={mode}>
                {polling.isOpen ? (
                  <Polling
                    content={content}
                    onChangeContent={handleChangeContent}
                    polling={polling}
                    onChangePolling={handleChangePolling}
                    onClosePolling={() => handleTogglePolling(false)}
                    isExpand={isExpand}
                  />
                ) : (
                  <>
                    <Textarea
                      id="stream-compose-textarea"
                      placeholder="Tulis ide kamu disini..."
                      value={content}
                      onChange={handleChangeContent}
                      data-cy="stream-compose-textarea"
                      isExpand={isExpand}
                    />
                    <ImageListContainer hasImage={images.length > 0}>
                      <ImageList
                        images={images}
                        onResetImage={(i) => handleResetImage(i)}
                      />
                    </ImageListContainer>
                    {/* @ts-expect-error */}
                    <Flex mb={documents.length > 0 ? '20px' : '0'}>
                      <FileContainer
                        documents={documents}
                        onSetDocuments={setDocuments}
                      />
                    </Flex>
                    {targetPrice.isOpen && (
                      <TargetPrice
                        targetPrice={targetPrice}
                        onChange={handleChangeTargetPrice}
                        onClose={() => handleToggleTargetPrice(false)}
                      />
                    )}
                  </>
                )}

                {mode === COMPOSE_MODE.REPOST && post && (
                  <RepostBox post={post} />
                )}
              </ComposePostContent>

              <Footer
                mode={mode}
                onCancel={handleCancel}
                onSubmit={handleSubmit}
                isLoading={isLoading}
                onSelectEmoji={handleSelectEmoji}
                onSelectGif={setImages}
                isOpenPolling={polling.isOpen}
                isPinned={pinnedPost}
                onTogglePolling={() => handleTogglePolling(!polling.isOpen)}
                onToggleTargetPrice={() =>
                  handleToggleTargetPrice(!targetPrice.isOpen)
                }
                onTogglePin={handleTogglePinned}
                documents={documents}
                onSetDocuments={setDocuments}
                onSetImages={setImages}
                onErrorMaxSize={setErrorModalMaxSizeVisible}
                images={images}
                isSubmitDisabled={content.length === 0}
                isPostComment={postTypeComment}
                showPinButton={showPinButton}
              />
            </ComposeContent>
          </Flex>
        </form>
        <ModalFailedPost
          onSubmit={(e) => handleSubmit(e)}
          onCancel={handleCloseFailedModal}
          visible={errorModalVisible}
          isLoading={isLoading}
        />
        <ModalMaximumSize
          onCancel={() => setErrorModalMaxSizeVisible(false)}
          visible={errorModalMaxSizeVisible}
        />
      </div>
    </ComposeBox>
  );
}

export default StreamCompose;
